import { useCallback } from 'react';
import {
  useCollectionsStore,
  setCollections,
  setSelectedRequest,
  setSelectedCollection,
  setSelectedFolder,
} from '../../store/collectionsStore';
import { useTabsStore } from '../../store/tabsStore';
import { useWorkspaceStore } from '../../store/workspaceStore';
import { useAuthStore } from '../../store/authStore';
import { setUpgradeReason, setUpgradeModalOpen } from '../../store/uiModalStore';
import { requireWritableWorkspace } from '../../plans/localGate';
import { requireApiCapacity, requireApiRoom } from '../../plans/localApiUsage';
import { collectionService } from '../../services/collectionService';
import { apiRequestService } from '../../services/apiRequestService';
import { isCloudRestWorkspace } from '../../utils/workspaceStorage';
import {
  addRequestDuplicateAfter,
  stripFolderIdForPayload,
  remapCollectionFolderIds,
  hydrateCollectionsFromApi,
  duplicateFolderInCollection,
} from '../../utils/collectionFolders';
import { resolveRequestRecord } from '../../utils/requestResolve';
import { prepareRequestBodyForApi } from '../../utils/requestPayload';
import { mapApiRequestToSidebar } from '../../utils/requestMappers';
import { generateLocalEntityId } from '../../utils/localIds';

/** Duplicating a request, a collection or a folder. */
export function useCollectionDuplicates({
  getLiveEditorStateForRequest,
  toastT,
  showToast,
  flashSidebarEntry,
  scheduleTreeSave,
  persistCollectionSettings,
}) {
  // ---- Duplication ----------------------------------------------------------
  /**
   * @param {Object}  request
   * @param {Object}  [opts]
   * @param {boolean} [opts.silent] suppress the per-item toast and flash — the
   *   BULK path sets this and reports once (see useBulkCollectionActions).
   * @returns {Promise<boolean>} whether the copy was made (for the bulk tally)
   */
  const duplicateRequest = useCallback(async (request, { silent = false } = {}) => {
    const collections = useCollectionsStore.getState().collections;
    const ws = useWorkspaceStore.getState().currentWorkspace;
    const authed = useAuthStore.getState().isAuthenticated;
    const parentCollection = collections.find((c) =>
      (c.requests || []).some((r) => String(r.id) === String(request.id))
    );
    if (!parentCollection) {
      if (!silent) toastT('toast.parentCollectionNotFound', 'error');
      return false;
    }

    const copyName = `${request.name} Copy`;
    const cloud = isCloudRestWorkspace(ws, authed);
    if (!requireWritableWorkspace(ws) || !(await requireApiCapacity('requests'))) return false;

    try {
      const source =
        (await resolveRequestRecord({
          requestId: request.id,
          collections,
          requestTabs: useTabsStore.getState().requestTabs,
          activeRequestTabId: useTabsStore.getState().activeRequestTabId,
          fetchFullRecord: cloud ? (id) => apiRequestService.getRequest(id) : undefined,
          liveEditorState: getLiveEditorStateForRequest(request.id),
        })) ?? request;

      if (cloud && ws) {
        const created = await apiRequestService.createRequest(
          prepareRequestBodyForApi(source, parentCollection.id, copyName)
        );
        let mapped = mapApiRequestToSidebar(created);
        // API may omit folder_id in the create response — keep the source placement.
        const sourceFolderId = source.folder_id ?? source.settings?.folder_id ?? null;
        if (sourceFolderId && !mapped.folder_id) {
          const { settings, folder_id } = stripFolderIdForPayload({
            ...mapped,
            folder_id: sourceFolderId,
          });
          mapped = { ...mapped, folder_id, settings };
        }
        setCollections((prev) =>
          prev.map((c) =>
            String(c.id) === String(parentCollection.id)
              ? addRequestDuplicateAfter(c, request.id, mapped)
              : c
          )
        );
        flashSidebarEntry(mapped.id);
        if (!silent) toastT('toast.requestDuplicated', 'success', { name: copyName });
      } else {
        const { settings, folder_id } = stripFolderIdForPayload(source);
        const localCopy = {
          ...source,
          // Not `Date.now()`: a bulk duplicate makes several of these inside one
          // millisecond, and two rows with one id are one row to every `find`.
          id: generateLocalEntityId('req'),
          name: copyName,
          folder_id,
          settings,
        };
        setCollections((prev) =>
          prev.map((c) =>
            String(c.id) === String(parentCollection.id)
              ? addRequestDuplicateAfter(c, request.id, localCopy)
              : c
          )
        );
        flashSidebarEntry(localCopy.id);
        if (!silent) toastT('toast.requestDuplicatedLocal', 'info', { name: copyName });
      }
      return true;
    } catch (error) {
      console.error('Failed to duplicate request:', error);
      // The plan limit is said even in a silent (bulk) run: it is WHY the rest of
      // the batch will fail too, and the upgrade modal is the way out.
      if (error.response?.data?.upgrade_required) {
        const reason = error.response.data.error || 'Upgrade required to add more.';
        setUpgradeReason(reason);
        setUpgradeModalOpen(true);
        showToast(reason, 'error');
        return false;
      }
      if (!silent) {
        toastT('toast.duplicateRequestFailed', 'error', {
          message: error.response?.data?.error || error.message,
        });
      }
      return false;
    }
  }, [toastT, showToast, getLiveEditorStateForRequest, flashSidebarEntry]);

  /** @see duplicateRequest for `opts.silent` and the boolean return. */
  const duplicateCollection = async (collection, { silent = false } = {}) => {
    const ws = useWorkspaceStore.getState().currentWorkspace;
    const authed = useAuthStore.getState().isAuthenticated;
    const copyName = `${collection.name} Copy`;
    // Place the duplicate immediately after its source in the list (like schema
    // duplication) instead of appending it at the end.
    const insertAfterSource = (list, copy) => {
      const idx = list.findIndex((c) => String(c.id) === String(collection.id));
      return idx === -1
        ? [...list, copy]
        : [...list.slice(0, idx + 1), copy, ...list.slice(idx + 1)];
    };

    // Plans v2: a copy is one more collection and all of its requests.
    if (!requireWritableWorkspace(ws)) return false;
    if (!(await requireApiRoom({ collections: 1, requests: (collection.requests || []).length }))) return false;

    try {
      if (isCloudRestWorkspace(ws, authed) && ws) {
        let source = collection;
        try {
          const full = await collectionService.getCollection(collection.id);
          const [hydrated] = hydrateCollectionsFromApi([full]);
          source = hydrated || full;
        } catch (loadErr) {
          console.warn('Duplicate: using sidebar collection data', loadErr);
        }

        // Folders are JSON inside `settings`, so copying the settings copied the
        // folder IDS as well — and a folder id has to name ONE folder in the
        // workspace (drag targets and the selection index are keyed by it).
        // See remapCollectionFolderIds.
        const { collection: remapped } = remapCollectionFolderIds(source);

        const newCollection = await collectionService.createCollection({
          name: copyName,
          description: source.description || '',
          workspace_id: ws.id,
          settings: remapped.settings || {},
        });

        const duplicatedRequests = [];
        let creationError = null;
        for (const req of remapped.requests || []) {
          try {
            const created = await apiRequestService.createRequest(
              prepareRequestBodyForApi(req, newCollection.id, req.name)
            );
            duplicatedRequests.push(mapApiRequestToSidebar(created));
          } catch (reqError) {
            // Stop on the first failure (e.g. a per-collection request limit) and
            // commit the partial result below so local state matches what the
            // server actually persisted, then surface the error.
            creationError = reqError;
            break;
          }
        }

        const copy = { ...newCollection, requests: duplicatedRequests };
        const nextCollections = insertAfterSource(useCollectionsStore.getState().collections, copy);
        setCollections(nextCollections);
        scheduleTreeSave(nextCollections);
        if (creationError) throw creationError;
        if (!silent) toastT('toast.collectionDuplicated', 'success', { name: copyName });
      } else {
        const { collection: remapped } = remapCollectionFolderIds(collection);
        const localCopy = {
          ...remapped,
          id: generateLocalEntityId('col'),
          name: copyName,
          requests: (remapped.requests || []).map((r) => ({
            ...r,
            id: generateLocalEntityId('req'),
          })),
        };
        setCollections((prev) => insertAfterSource(prev, localCopy));
        if (!silent) toastT('toast.collectionDuplicatedLocal', 'info', { name: copyName });
      }
      return true;
    } catch (error) {
      console.error('Failed to duplicate collection:', error);
      if (error.response?.data?.upgrade_required) {
        const reason = error.response.data.error || 'Upgrade required to add more.';
        setUpgradeReason(reason);
        setUpgradeModalOpen(true);
        showToast(reason, 'error');
        return false;
      }
      if (!silent) {
        toastT('toast.duplicateCollectionFailed', 'error', {
          message: error.response?.data?.error || error.message,
        });
      }
      return false;
    }
  };

  /** @see duplicateRequest for `opts.silent` and the boolean return. */
  const duplicateFolder = async (collection, folder, { silent = false } = {}) => {
    const ws = useWorkspaceStore.getState().currentWorkspace;
    const authed = useAuthStore.getState().isAuthenticated;
    const collectionId = collection.id;
    // Read the collection FRESH: in a bulk run the `collection` argument is the
    // object the selection was built from, and an earlier copy in the same run
    // has already added rows to the store. Building on the stale one would drop
    // them when this result is written back.
    const current =
      useCollectionsStore.getState().collections.find((c) => String(c.id) === String(collectionId)) ||
      collection;
    const result = duplicateFolderInCollection(current, folder.id);
    if (!result) return false;

    const { collection: next, addedRequests, copyName, rootFolderId } = result;
    // Plans v2: the copied folder's requests count.
    if (!requireWritableWorkspace(ws) || !(await requireApiRoom({ requests: addedRequests.length }))) {
      return false;
    }

    try {
      if (isCloudRestWorkspace(ws, authed) && ws) {
        await persistCollectionSettings(collectionId, next.settings);
        const createdRequests = [];
        let creationError = null;
        for (const req of addedRequests) {
          try {
            const created = await apiRequestService.createRequest(
              prepareRequestBodyForApi(req, collectionId, req.name)
            );
            createdRequests.push(mapApiRequestToSidebar(created));
          } catch (reqError) {
            // Stop on the first failure (e.g. a per-collection request limit).
            // The folders are already persisted, so commit the requests that did
            // get created below — keeping local state in sync with the server —
            // then surface the error.
            creationError = reqError;
            break;
          }
        }
        const keptRequests = (current.requests || []).filter(
          (r) => !addedRequests.some((added) => String(added.id) === String(r.id))
        );
        const finalCollection = {
          ...next,
          requests: [...keptRequests, ...createdRequests],
        };
        setCollections((prev) =>
          prev.map((c) => (String(c.id) === String(collectionId) ? finalCollection : c))
        );
        const copiedRoot = finalCollection.settings?.folders?.find(
          (f) => String(f.id) === String(rootFolderId)
        );
        if (copiedRoot) {
          setSelectedFolder({ ...copiedRoot, collectionId });
          setSelectedRequest(null);
          setSelectedCollection(null);
        }
        flashSidebarEntry(rootFolderId);
        if (creationError) throw creationError;
        if (!silent) toastT('toast.folderDuplicated', 'success', { name: copyName });
      } else {
        setCollections((prev) =>
          prev.map((c) => (String(c.id) === String(collectionId) ? next : c))
        );
        const copiedRoot = next.settings?.folders?.find(
          (f) => String(f.id) === String(rootFolderId)
        );
        if (copiedRoot) {
          setSelectedFolder({ ...copiedRoot, collectionId });
          setSelectedRequest(null);
          setSelectedCollection(null);
        }
        flashSidebarEntry(rootFolderId);
        if (!silent) toastT('toast.folderDuplicatedLocal', 'info', { name: copyName });
      }
      return true;
    } catch (error) {
      console.error('Failed to duplicate folder:', error);
      if (error.response?.data?.upgrade_required) {
        const reason = error.response.data.error || 'Upgrade required to add more.';
        setUpgradeReason(reason);
        setUpgradeModalOpen(true);
        showToast(reason, 'error');
        return false;
      }
      if (!silent) {
        toastT('toast.duplicateFolderFailed', 'error', {
          message: error.response?.data?.error || error.message,
        });
      }
      return false;
    }
  };

  return { duplicateRequest, duplicateCollection, duplicateFolder };
}
